import { useMemo } from 'react';

import { LogicalOperator } from '@frontend/api';
import { Box, Divider } from '@frontend/legacy-components';

import { BetweenItemDropBorder } from 'components/BetweenItemDropBorder';
import {
  CUSTOM_DRAGGABLE_ITEM_TYPES,
  DROP_RESULT_TYPES,
} from 'components/customDragLayer';
import { useDrop } from 'components/customDragLayer/hooks/useDrop';
import type {
  DraggableItem,
  DropResult,
} from 'components/customDragLayer/types';

import { NamedDivider } from './NamedDivider';

type AcceptableItemType = typeof CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_FILTER_GROUP;

type BetweenGroupDropBorderProps = {
  side: 'top' | 'bottom';
  signalIndex: number;
  groupIndex: number;
  onReorderGroups: (
    oldSignalIndex: number,
    oldGroupIndex: number,
    newSignalIndex: number,
    newGroupIndex: number,
  ) => void;
  isFirstGroup: boolean;
  isLastGroup: boolean;
  logicalOperator: LogicalOperator;
  isPlaceholder?: boolean;
};

export const BetweenGroupDropBorder = ({
  side,
  signalIndex,
  groupIndex,
  onReorderGroups,
  isFirstGroup,
  isLastGroup,
  logicalOperator,
  isPlaceholder = false,
}: BetweenGroupDropBorderProps) => {
  const handleDrop = (
    droppedItem: DraggableItem<AcceptableItemType>,
  ): DropResult | undefined => {
    if (
      droppedItem.draggableItemType ===
      CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_FILTER_GROUP
    ) {
      const oldSignalIndex = droppedItem.group.signalIndex;
      const oldGroupIndex = droppedItem.group.groupIndex;
      const newSignalIndex = signalIndex;
      const newGroupIndex = groupIndex;

      // Check if dropping in same position (no-op)
      if (
        oldSignalIndex === newSignalIndex &&
        (newGroupIndex === oldGroupIndex || newGroupIndex === oldGroupIndex + 1)
      ) {
        const dropResultType =
          oldSignalIndex === newSignalIndex
            ? DROP_RESULT_TYPES.RULES_REORDER_FILTER_GROUPS
            : DROP_RESULT_TYPES.RULES_MOVE_FILTER_GROUP_BETWEEN_SIGNALS;

        return {
          dropResultType,
          didReorder: false,
        };
      }

      onReorderGroups(
        oldSignalIndex,
        oldGroupIndex,
        newSignalIndex,
        newGroupIndex,
      );

      const dropResultType =
        oldSignalIndex === newSignalIndex
          ? DROP_RESULT_TYPES.RULES_REORDER_FILTER_GROUPS
          : DROP_RESULT_TYPES.RULES_MOVE_FILTER_GROUP_BETWEEN_SIGNALS;

      return {
        dropResultType,
        didReorder: true,
      };
    }

    return undefined;
  };

  const [{ canDrop, isOver }, connectDropTarget] = useDrop({
    accept: CUSTOM_DRAGGABLE_ITEM_TYPES.RULES_FILTER_GROUP,
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver({ shallow: true }),
    }),
    onDropEnd: handleDrop,
  });

  // Memoize common props for BetweenItemDropBorder components
  const commonDropBorderProps = useMemo(
    () => ({
      ref: connectDropTarget,
      side,
      canDrop,
      isOver,
      dropAreaProps: { height: 5 },
    }),
    [connectDropTarget, side, canDrop, isOver],
  );

  // Calculate opacity for dividers when drop area is active
  const dividerOpacity = isOver ? 0 : 1;

  return isPlaceholder ? (
    <BetweenItemDropBorder {...commonDropBorderProps} />
  ) : side === 'top' ? (
    <>
      {isFirstGroup ? (
        <BetweenItemDropBorder {...commonDropBorderProps} />
      ) : (
        <Box position="relative">
          <NamedDivider
            label={logicalOperator}
            opacity={dividerOpacity}
            transition="opacity 150ms ease"
          />
          <BetweenItemDropBorder
            {...commonDropBorderProps}
            centerInContainer={true}
          />
        </Box>
      )}
    </>
  ) : side === 'bottom' && isLastGroup ? (
    <Box position="relative">
      <Divider
        variant="dashed"
        opacity={dividerOpacity}
        transition="opacity 150ms ease"
      />
      <BetweenItemDropBorder
        {...commonDropBorderProps}
        centerInContainer={true}
      />
    </Box>
  ) : null;
};
